/**
 * 用户头像 + 退出登录
 *
 * 显示于 Titlebar 最右侧：
 *  - 欢迎文字 + 字母头像（取用户名首字母）
 *  - 点击头像展开下拉菜单（通过 Portal 渲染到 body，绕开 overflow-hidden 裁剪）
 */

import { createSignal, Show } from "solid-js"
import { Portal } from "solid-js/web"
import { getCurrentUser, goToLogin } from "@/utils/zd-auth"

// ─── 退出图标 ─────────────────────────────────────────────────────────────────

function LogoutIcon(props: { class?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class={props.class}
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  )
}

// ─── 主组件 ───────────────────────────────────────────────────────────────────

export function UserAvatarButton() {
  const user = getCurrentUser()
  if (!user) return null

  const [open, setOpen] = createSignal(false)
  // 下拉菜单的定位坐标（fixed 定位，相对于 viewport）
  const [dropPos, setDropPos] = createSignal({ top: 0, right: 0 })

  let buttonRef: HTMLButtonElement | undefined

  /** 打开时先计算坐标，再切换状态 */
  const toggle = () => {
    if (!open() && buttonRef) {
      const rect = buttonRef.getBoundingClientRect()
      setDropPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
      })
    }
    setOpen((v: boolean) => !v)
  }

  const handleLogout = () => {
    setOpen(false)
    goToLogin()
  }

  const initial = () => user.username.charAt(0).toUpperCase()

  return (
    <div class="relative flex items-center gap-2 ml-1 select-none">
      {/* 欢迎文字（中等屏及以上显示） */}
      <span class="hidden md:block text-sm text-v2-text-text-secondary whitespace-nowrap leading-none">
        欢迎回来，{user.username}
      </span>

      {/* 字母头像按钮 */}
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-label={`用户菜单：${user.username}`}
        aria-expanded={open()}
        aria-haspopup="menu"
        class="w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center shrink-0 transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50"
      >
        {initial()}
      </button>

      {/* Portal：渲染到 body，彻底绕开 Titlebar 的 overflow-hidden */}
      <Show when={open()}>
        <Portal>
          {/* 遮罩（点击关闭） */}
          <div
            class="fixed inset-0 z-[9998]"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          {/* 下拉菜单（样式参考 model-evaluation-platform Layout.tsx：w-36 + p-1 单菜单项） */}
          <div
            role="menu"
            style={{
              position: "fixed",
              top: `${dropPos().top}px`,
              right: `${dropPos().right}px`,
            }}
            class="z-[9999] w-36 rounded-md border border-slate-200 bg-white p-1 shadow-md"
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              class="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 focus-visible:bg-slate-100 focus-visible:outline-none transition-colors cursor-pointer select-none"
            >
              <LogoutIcon class="w-4 h-4 text-slate-500 shrink-0" />
              退出登录
            </button>
          </div>
        </Portal>
      </Show>
    </div>
  )
}
